import type { QueryClient } from "@tanstack/react-query";
import type {
  Connection,
  ConnectionConflict,
  ConnectionDirection,
  ConnectionStatus,
  ProblemDetails,
  SetConnectionShareRequest,
  ShareEntry,
  ShareLibraryIds,
  ShareRestrictions,
  UserLookupResponse,
} from "@rago/web-shared";
import type { Library } from "../sources";
import { api } from "./apiClient";
import { exactCount } from "./formatNumber";
import type { NavServer } from "./navServers";
import { shortDate } from "./timeAgo";

/**
 * Sharing wire client (ARCHITECTURE §4a) — a CONNECTION between two
 * accounts, Plex's friends: requested by one, accepted once by the other,
 * then either side shares libraries from the servers they own without
 * asking again, and either side can break it (both directions go). The
 * media server only ever sees the resulting grants via sync. One query
 * key feeds the Sharing page, the person page and the rail's badge, so
 * they never disagree after a mutation.
 */

interface Envelope<T> {
  meta: { offset: number; limit: number; total: number };
  items: T[];
}

export const connectionsKey = ["connections"] as const;

/** Exact-match account lookup (email or username); 404 → ApiProblemError. */
export function lookupUser(q: string): Promise<UserLookupResponse> {
  return api<UserLookupResponse>(`/v1/users/lookup?q=${encodeURIComponent(q)}`);
}

export async function fetchConnections(): Promise<Connection[]> {
  const res = await api<Envelope<Connection>>("/v1/connections");
  return res.items;
}

export interface ShareRequest {
  serverId: string;
  libraryIds: ShareLibraryIds;
  /** Absent = the broker's default (downloads allowed). */
  restrictions?: ShareRestrictions;
}

/** ONE invitation: the connection plus what my servers share from the start. */
export function inviteConnection(userId: string, shares: ShareRequest[]): Promise<Connection> {
  return api<Connection>("/v1/connections", { body: { userId, shares } });
}

export function acceptConnection(id: string): Promise<void> {
  return api<void>(`/v1/connections/${id}/accept`, { method: "POST" });
}

/** Decline (they asked), cancel (I asked) or disconnect (connected): one call. */
export function deleteConnection(id: string): Promise<void> {
  return api<void>(`/v1/connections/${id}`, { method: "DELETE" });
}

/** Share one of my servers, or change what it shares: the libraries, and
 * the person's restrictions when sent (absent = what is stored stays). */
export function putServerAccess(
  connectionId: string,
  serverId: string,
  body: SetConnectionShareRequest,
): Promise<ShareEntry> {
  return api<ShareEntry>(`/v1/connections/${connectionId}/servers/${serverId}`, {
    method: "PUT",
    body,
  });
}

export function deleteServerAccess(connectionId: string, serverId: string): Promise<void> {
  return api<void>(`/v1/connections/${connectionId}/servers/${serverId}`, { method: "DELETE" });
}

/**
 * Post-write fan-out. Accepting and disconnecting change what
 * /v1/resources lists for this account (their servers appear or go), so
 * `resources` also refreshes the server list and the nav; sharing one of my
 * own servers changes nothing I can reach.
 */
export async function invalidateSharing(
  qc: QueryClient,
  opts: { resources?: boolean } = {},
): Promise<void> {
  await Promise.all([
    qc.invalidateQueries({ queryKey: connectionsKey }),
    ...(opts.resources
      ? [
          qc.invalidateQueries({ queryKey: ["resources"] }),
          qc.invalidateQueries({ queryKey: ["nav", "servers"] }),
        ]
      : []),
  ]);
}

// ── Pure helpers (tested in sharing.test.ts) ──────────────────────────

/**
 * One server's share as the pages edit it. `"*"` = every library,
 * including ones added later (Kenny, 2026-10-14: all on means new ones
 * too — the card no longer spells it out, at his word); a list = exactly
 * those; `null` = nothing shared there. Library ids are strings on the
 * server's own API and integers on the broker's wire.
 */
export type Selection = "*" | string[];

/** Non-numeric ids are dropped (nothing to grant); an empty list is no share. */
export function toWire(sel: Selection | null): ShareLibraryIds | null {
  if (sel === null) return null;
  if (sel === "*") return "*";
  const ids = sel.map(Number).filter((n) => Number.isSafeInteger(n) && n >= 0);
  return ids.length > 0 ? ids : null;
}

export function fromWire(ids: ShareLibraryIds | undefined): Selection | null {
  if (ids === undefined) return null;
  return ids === "*" ? "*" : ids.map(String);
}

export function isLibraryOn(sel: Selection | null, id: string): boolean {
  return sel === "*" || (sel !== null && sel.includes(id));
}

/**
 * Flip one library's switch. `allIds` is the server's list as it stands:
 * with every one of them on the share becomes `"*"` (so what is on screen
 * and what is stored never differ: all on includes libraries added later),
 * with none it is `null`, else the listed ones in the server's order. An
 * id the server no longer lists (a library deleted since) is let go.
 */
export function toggleLibrary(
  sel: Selection | null,
  id: string,
  on: boolean,
  allIds: readonly string[],
): Selection | null {
  const wanted = new Set(sel === "*" ? allIds : (sel ?? []));
  if (on) wanted.add(id);
  else wanted.delete(id);
  const listed = allIds.filter((x) => wanted.has(x));
  if (listed.length === 0) return null;
  return listed.length === allIds.length ? "*" : listed;
}

/** How many of the listed libraries are on; null when the list is unknown. */
export function countOn(sel: Selection | null, allIds: readonly string[] | null): number | null {
  if (allIds === null) return null;
  if (sel === "*") return allIds.length;
  if (sel === null) return 0;
  return allIds.filter((id) => sel.includes(id)).length;
}

export function sameSelection(a: Selection | null, b: Selection | null): boolean {
  if (a === b) return true;
  if (a === null || b === null || a === "*" || b === "*") return false;
  return a.length === b.length && [...a].sort().join() === [...b].sort().join();
}

function librariesWord(n: number): string {
  return n === 1 ? "1 library" : `${n} libraries`;
}

/** "All libraries" or "N libraries" — the count phrasing. */
export function libraryCountLabel(ids: ShareLibraryIds): string {
  return ids === "*" ? "All libraries" : librariesWord(ids.length);
}

/** A share in words when its libraries cannot be listed (an offline server). */
export function selectionLine(sel: Selection | null): string {
  if (sel === null) return "Nothing shared";
  return `${sel === "*" ? "All libraries" : librariesWord(sel.length)} shared`;
}

/** Beside a server's name on its card: how many libraries it has — "All 6
 * libraries" while every one of them is on. */
export function serverCountLine(sel: Selection | null, total: number): string {
  if (total === 0) return "No libraries";
  return sel === "*" && total > 1 ? `All ${total} libraries` : librariesWord(total);
}

/** The person page's count, right of its label; "" when no server can be
 * asked for its libraries. */
export function counterLine(on: number, total: number): string {
  if (total === 0) return "";
  return `${on} of ${total} ${total === 1 ? "library" : "libraries"}`;
}

/**
 * What I share with one person, as their page edits it (the page's draft
 * under the "Unsaved changes" pill): one selection per server I own, and
 * the person's "Allow downloads" switch — ONE per person, applied to every
 * server shared with them, since a restriction is a trust decision about
 * the person (Plex's Restrictions tab is per person too). The broker keeps
 * it per share; the page keeps the shares agreeing.
 */
export interface AccessForm {
  readonly access: Readonly<Record<string, Selection | null>>;
  readonly allowDownloads: boolean;
}

/**
 * The saved state of that form, from the connection. Downloads are allowed
 * unless a share says otherwise — so true while nothing is shared, and a
 * mix (another client set one server apart) reads as off until Save makes
 * every share agree.
 */
export function accessBaseline(
  given: readonly ShareEntry[],
  ownedServerIds: readonly string[],
): AccessForm {
  const access: Record<string, Selection | null> = {};
  for (const id of ownedServerIds) {
    access[id] = fromWire(given.find((e) => e.serverId === id)?.libraryIds);
  }
  return { access, allowDownloads: given.every((e) => e.restrictions.allowDownloads) };
}

export function sameAccess(a: AccessForm, b: AccessForm): boolean {
  if (a.allowDownloads !== b.allowDownloads) return false;
  const ids = new Set([...Object.keys(a.access), ...Object.keys(b.access)]);
  for (const id of ids) {
    if (!sameSelection(a.access[id] ?? null, b.access[id] ?? null)) return false;
  }
  return true;
}

/** One server's PUT (or DELETE, for a null selection) as Save sends it. */
export interface AccessChange {
  serverId: string;
  selection: Selection | null;
  allowDownloads: boolean;
}

/**
 * What Save sends, one per server: every server whose selection differs
 * from what is saved and, when the downloads switch moved, every server
 * that is shared (the flag rides the share's own PUT; a server with
 * nothing shared has no share to carry it). Each change names the flag the
 * form shows, so a share saved for its libraries agrees with the switch.
 */
export function accessChanges(saved: AccessForm, draft: AccessForm): AccessChange[] {
  const flagMoved = draft.allowDownloads !== saved.allowDownloads;
  return Object.keys(draft.access)
    .filter((id) => {
      const selection = draft.access[id] ?? null;
      return !sameSelection(selection, saved.access[id] ?? null) || (flagMoved && selection !== null);
    })
    .map((id) => ({
      serverId: id,
      selection: draft.access[id] ?? null,
      allowDownloads: draft.allowDownloads,
    }));
}

/** "3,067 movies", "1 show"; "" for a server too old to count. */
export function itemCountLabel(lib: Pick<Library, "type" | "itemCount">, locale?: string): string {
  const n = lib.itemCount;
  if (typeof n !== "number") return "";
  const noun = lib.type === "show" ? (n === 1 ? "show" : "shows") : n === 1 ? "movie" : "movies";
  return `${exactCount(n, locale)} ${noun}`;
}

/**
 * A server's libraries as this device can list them right now, or null:
 * the broker says it is offline, or it did not answer from here. Library
 * names never come from the broker (it must not know them — TV §7).
 */
export function listedLibraries(
  nav: readonly NavServer[] | undefined,
  server: { id: string; online: boolean } | undefined,
): Library[] | null {
  if (!server?.online) return null;
  const entry = nav?.find((n) => n.id === server.id);
  return entry && !entry.failed ? entry.libraries : null;
}

/** serverId → how many libraries it lists, for the servers that can be asked. */
export type LibraryCounts = ReadonlyMap<string, number>;

export function libraryCounts(
  nav: readonly NavServer[] | undefined,
  servers: readonly { id: string; online: boolean }[],
): LibraryCounts {
  const counts = new Map<string, number>();
  for (const s of servers) {
    const libs = listedLibraries(nav, s);
    if (libs) counts.set(s.id, libs.length);
  }
  return counts;
}

function newestFirst(key: (c: Connection) => string) {
  return (a: Connection, b: Connection) => key(b).localeCompare(key(a)) || b.id.localeCompare(a.id);
}

/**
 * Requests = who asked to connect with me; people = everyone else, in the
 * list's order: the connected ones, newest connection first, then the
 * invites I sent that still wait, newest first.
 */
export function splitConnections(items: Connection[]): {
  requests: Connection[];
  people: Connection[];
} {
  const requests = items
    .filter((c) => c.status === "pending" && c.direction === "received")
    .sort(newestFirst((c) => c.createdAt));
  const connected = items
    .filter((c) => c.status === "accepted")
    .sort(newestFirst((c) => c.respondedAt ?? c.createdAt));
  const invited = items
    .filter((c) => c.status === "pending" && c.direction === "sent")
    .sort(newestFirst((c) => c.createdAt));
  return { requests, people: [...connected, ...invited] };
}

/** One direction's cell in a People row. `quiet` = nothing there: dim, no weight. */
export interface ShareColumn {
  primary: string;
  secondary?: string;
  quiet: boolean;
}

const everyLibrary = (entries: readonly ShareEntry[]) => entries.every((e) => e.libraryIds === "*");

/** The libraries these shares add up to; null when an all-libraries share
 * sits on a server that cannot be asked how many it has. */
function totalLibraries(entries: readonly ShareEntry[], counts: LibraryCounts): number | null {
  let n = 0;
  for (const e of entries) {
    if (e.libraryIds !== "*") {
      n += e.libraryIds.length;
      continue;
    }
    const known = counts.get(e.serverId);
    if (!known) return null;
    n += known;
  }
  return n;
}

/**
 * What one side shares, in a People row's column: "2 libraries / on 2
 * servers", "1 library / on Test Box", "2 libraries / from Maya's NAS",
 * "Nothing yet". Shares that are ALL of their servers say "All libraries"
 * rather than today's count — it is what they are (libraries added later
 * come too), and the words never change once the servers have answered.
 * An invite still waiting says "once they accept" under what it carries,
 * and a dash where they cannot have shared yet.
 */
export function shareColumn(
  entries: readonly ShareEntry[],
  counts: LibraryCounts,
  side: "given" | "received",
  pending = false,
): ShareColumn {
  if (entries.length === 0) {
    return { primary: pending && side === "received" ? "—" : "Nothing yet", quiet: true };
  }
  const total = everyLibrary(entries) ? null : totalLibraries(entries, counts);
  const primary =
    total !== null ? librariesWord(total) : everyLibrary(entries) ? "All libraries" : "Libraries";
  const where = entries.length === 1 ? entries[0]!.serverName : `${entries.length} servers`;
  return {
    primary,
    secondary: pending ? "once they accept" : `${side === "given" ? "on" : "from"} ${where}`,
    quiet: false,
  };
}

/** Both directions on one line, for a row too narrow for the columns. */
export function narrowShareLine(c: Connection, counts: LibraryCounts): string {
  const short = (entries: readonly ShareEntry[]): string => {
    if (entries.length === 0) return "nothing";
    if (everyLibrary(entries)) return "all";
    const total = totalLibraries(entries, counts);
    return total !== null ? String(total) : "some";
  };
  if (c.status === "pending") return `You share ${short(c.given)} once they accept`;
  return `You share ${short(c.given)} · They share ${short(c.received)}`;
}

function parse(iso: string): Date | null {
  const d = new Date(iso);
  return Number.isFinite(d.getTime()) ? d : null;
}

/** "Oct 2" inside the current year, "Dec 30, 2025" beyond it. */
function monthDay(iso: string, now: number, locale?: string): string {
  const d = parse(iso);
  if (!d) return "";
  if (d.getFullYear() === new Date(now).getFullYear()) {
    return new Intl.DateTimeFormat(locale, { month: "short", day: "numeric" }).format(d);
  }
  return shortDate(iso, locale);
}

function startOfLocalDay(d: Date): number {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
}

/**
 * When a request came: "today", "yesterday", then the date. By LOCAL
 * calendar day, measured between midnights (lib/profile.ts's day rule), so
 * a request from 23:50 reads "yesterday" ten minutes later.
 */
export function askedWhen(iso: string, now: number = Date.now(), locale?: string): string {
  const d = parse(iso);
  if (!d) return "";
  const days = Math.round((startOfLocalDay(new Date(now)) - startOfLocalDay(d)) / 86_400_000);
  if (days <= 0) return "today";
  if (days === 1) return "yesterday";
  return monthDay(iso, now, locale);
}

/** Under a person's name in the list: who, and since when. */
export function personSubLine(c: Connection, now: number = Date.now(), locale?: string): string {
  const who = `@${c.user.username}`;
  if (c.status === "accepted") {
    return `${who} · Since ${monthDay(c.respondedAt ?? c.createdAt, now, locale)}`;
  }
  if (c.direction === "sent") return `${who} · Sent ${monthDay(c.createdAt, now, locale)}`;
  return `${who} · Asked ${askedWhen(c.createdAt, now, locale)}`;
}

/** Under the name on the person's own page. */
export function personStatusLine(c: Connection, now: number = Date.now(), locale?: string): string {
  const who = `@${c.user.username}`;
  if (c.status === "accepted") {
    return `${who} · Connected since ${monthDay(c.respondedAt ?? c.createdAt, now, locale)}`;
  }
  if (c.direction === "sent") return `${who} · Invite sent ${monthDay(c.createdAt, now, locale)}`;
  return `${who} · Asked ${askedWhen(c.createdAt, now, locale)}`;
}

const ACCEPTING_OPENS = "Accepting lets you share libraries with each other.";

/** What a request offers once accepted ("Offers 2 libraries from Lena's
 * NAS"); "" for one that carries nothing. */
function offerOf(c: Connection, counts: LibraryCounts): string {
  if (c.received.length === 0) return "";
  const offer = shareColumn(c.received, counts, "received");
  const what = offer.primary.charAt(0).toLowerCase() + offer.primary.slice(1);
  return `Offers ${what} ${offer.secondary ?? ""}`.trimEnd();
}

/**
 * A request card's line: who asked, when, and what they offer once
 * accepted — or, for a request that carries nothing, what accepting opens.
 */
export function requestLine(
  c: Connection,
  counts: LibraryCounts,
  now: number = Date.now(),
  locale?: string,
): string {
  const asked = `@${c.user.username} · Asked ${askedWhen(c.createdAt, now, locale)}`;
  const offer = offerOf(c, counts);
  return offer ? `${asked} · ${offer}` : `${asked}. ${ACCEPTING_OPENS}`;
}

/** The same request as a sentence, for the person's own page (their name
 * and when they asked are already above it). */
export function requestSentence(c: Connection, counts: LibraryCounts): string {
  const name = c.user.displayName || c.user.username;
  const offer = offerOf(c, counts);
  return `${name} wants to connect. ${offer ? `${offer}.` : ACCEPTING_OPENS}`;
}

/**
 * The connections list with one of MY servers' shares replaced by the
 * broker's answer (or dropped): what a save writes straight into the
 * query, so the page never waits on a refetch to agree with itself.
 */
export function patchGiven(
  list: readonly Connection[],
  connectionId: string,
  serverId: string,
  entry: ShareEntry | null,
): Connection[] {
  return list.map((c) => {
    if (c.id !== connectionId) return c;
    const had = c.given.some((e) => e.serverId === serverId);
    if (entry === null) return { ...c, given: c.given.filter((e) => e.serverId !== serverId) };
    return {
      ...c,
      given: had ? c.given.map((e) => (e.serverId === serverId ? entry : e)) : [...c.given, entry],
    };
  });
}

export interface ConflictCopy {
  text: string;
  /** The person already connected: the words point at their page. */
  link: boolean;
}

/** What to say about a connection the pair already has. */
export function existingCopy(
  info: { status: ConnectionStatus; direction: ConnectionDirection },
  name: string,
): ConflictCopy {
  if (info.status === "accepted") {
    return { text: `Already connected — open ${name}'s page to change what they can see.`, link: true };
  }
  if (info.direction === "sent") return { text: `You've already invited ${name}.`, link: false };
  return { text: `${name} has invited you — accept their invitation on the Sharing page.`, link: false };
}

/** The 409 of POST /v1/connections, in words; any other problem in its own. */
export function conflictCopy(problem: ProblemDetails, name: string): ConflictCopy {
  const conn = (problem as ProblemDetails & Partial<ConnectionConflict>).connection;
  if (conn) return existingCopy(conn, name);
  return { text: problem.detail ?? problem.title, link: false };
}

export const RATE_LIMIT_COPY = "Too many invitations today. Try again tomorrow.";
